import { profileApi } from '../api/profile.js';

const PUBLIC_ROUTES = new Set(['/', '/login', '/register', '/about', '/product', '/terms', '/auth/google/callback']);

const routes = [
  { exact: '/',           loader: () => import('../pages/landing.js') },
  { exact: '/login',      loader: () => import('../pages/login.js') },
  { exact: '/register',   loader: () => import('../pages/register.js') },
  { exact: '/about',      loader: () => import('../pages/about.js') },
  { exact: '/product',    loader: () => import('../pages/product.js') },
  { exact: '/terms',      loader: () => import('../pages/terms.js') },
  { exact: '/workspaces', loader: () => import('../pages/workspace-picker.js') },
  { exact: '/assets',       loader: () => import('../pages/asset-list.js') },
  { exact: '/asset-types', loader: () => import('../pages/asset-type-manager.js') },
  { suffix: '/graph',       loader: () => import('../pages/asset-graph.js') },
  { prefix: '/assets/',     loader: () => import('../pages/asset-detail.js') },
  { exact: '/contacts',          loader: () => import('../pages/contact-list.js') },
  { prefix: '/contacts/',        loader: () => import('../pages/contact-detail.js') },
  { exact: '/workspace',          loader: () => import('../pages/workspace-settings.js') },
  { exact: '/workspace/activity', loader: () => import('../pages/activity-log.js') },
  { exact: '/invite',             loader: () => import('../pages/accept-invite.js') },
  { exact: '/api-keys',          loader: () => import('../pages/api-keys.js') },
  { exact: '/webhook-endpoints', loader: () => import('../pages/webhook-endpoints.js') },
  { exact: '/api-docs',          loader: () => import('../pages/api-docs.js') },
  { exact: '/tasks',             loader: () => import('../pages/action-items.js') },
  { exact: '/calendar',         loader: () => import('../pages/calendar.js') },
  { exact: '/notifications',     loader: () => import('../pages/notifications.js') },
  { exact: '/dashboard',         loader: () => import('../pages/dashboard.js') },
  { exact: '/profile',           loader: () => import('../pages/profile.js') },
  { exact: '/companies',         loader: () => import('../pages/companies.js') },
  { prefix: '/companies/',       loader: () => import('../pages/company-detail.js') },
  { exact: '/leads',             loader: () => import('../pages/leads.js') },
  { exact: '/projects',          loader: () => import('../pages/projects.js') },
  { exact: '/invoices',          loader: () => import('../pages/invoices.js') },
  { exact: '/reports',          loader: () => import('../pages/reports.js') },
  { exact: '/email-templates',  loader: () => import('../pages/email-templates.js') },
  { exact: '/admin',            loader: () => import('../pages/admin.js') },
  { exact: '/inbox',            loader: () => import('../pages/activity-inbox.js') },
];

function resolveLoader(hash) {
  for (const route of routes) {
    if (route.exact && route.exact === hash) return route.loader;
    if (route.suffix && hash.endsWith(route.suffix)) return route.loader;
    if (route.prefix && hash.startsWith(route.prefix)) return route.loader;
  }
  return routes.find((r) => r.exact === '/').loader;
}

export function createRouter(mountPoint) {
  async function navigate() {
    const raw = window.location.hash.slice(1) || '/';
    if (raw && !raw.startsWith('/')) return; // anchor link — don't route

    // Handle Google OAuth login callback — tokens arrive in query string
    if (raw.startsWith('/auth/google/callback')) {
      const params = new URLSearchParams(raw.split('?')[1] ?? '');
      const token   = params.get('token');
      const refresh = params.get('refresh');
      if (token && refresh) {
        const auth = window.Alpine?.store('auth');
        if (auth) {
          auth.setTokens(token, refresh);
          try {
            const { data } = await profileApi.get();
            auth.setUser(data.data);
            window.Alpine?.store('i18n')?.setLocale(data.data?.locale || 'en');
            window.location.hash = '/workspaces';
          } catch (_) {
            auth.logout();
            window.location.hash = '/login';
          }
        } else {
          window.location.hash = '/login';
        }
        return;
      }
      window.location.hash = '/login';
      return;
    }

    // Strip query params for route matching (but keep full raw for param extraction in pages)
    const hash     = raw.split('?')[0];
    const auth     = window.Alpine?.store('auth');

    if (!auth?.isAuthenticated && !PUBLIC_ROUTES.has(hash)) {
      window.location.hash = '/login';
      return;
    }

    if (auth?.isAuthenticated && !auth?.currentWorkspaceId && !PUBLIC_ROUTES.has(hash) && hash !== '/workspaces' && hash !== '/invite' && hash !== '/api-keys') {
      window.location.hash = '/workspaces';
      return;
    }

    const loader = resolveLoader(hash);
    const module = await loader();
    const componentFn = module.default || Object.values(module)[0];

    if (mountPoint._teardown) {
      mountPoint._teardown();
    }

    const preview = componentFn();
    const html = typeof preview.template === 'function'
      ? preview.template()
      : (preview.template ?? '');

    const name = `page_${Date.now()}`;
    mountPoint.innerHTML = `<div x-data="${name}" x-init="init()">${html}</div>`;
    window.Alpine.data(name, componentFn);
    window.Alpine.initTree(mountPoint.firstElementChild);
    requestAnimationFrame(() => window.lucide?.createIcons());

    mountPoint._teardown = () => {
      if (mountPoint.firstElementChild) {
        try { window.Alpine.destroyTree(mountPoint.firstElementChild); } catch (_) {}
      }
    };
  }

  window.addEventListener('hashchange', navigate);
  navigate();
}
